import React from 'react';
import { X, Check, AlertCircle, Shield, Wrench, TrendingUp, ThumbsUp, Info, Euro, Sparkles, DollarSign, Star } from 'lucide-react';
import equipmentDetails from '../../../config/equipmentDetails.json';

// Mapping entre les IDs d'actions et les IDs d'équipements
const ACTION_TO_EQUIPMENT_MAP: Record<string, string> = {
  'install-reinforced-door': 'door',
  'install-alarm-system': 'alarm',
  'install-cameras': 'camera',
  'install-smart-lock': 'smartLock',
  'install-outdoor-lighting': 'lighting',
  'install-video-doorbell': 'videoDoorbell',
  'install-window-locks': 'windowLocks',
  'install-motion-sensors': 'motionSensor',
  'install-door-sensors': 'doorSensor',
  'install-safe': 'safe',
  'install-window-bars': 'bars',
  'install-intercom': 'intercom',
  'install-fog-system': 'fogSystem',
  'secure-perimeter': 'securityGate',
  'upgrade-door-locks': 'multipointLock',
  'upgrade-alarm-monitoring': 'remoteSurveillance',
};

interface EquipmentDetailsModalProps {
  equipmentId: string;
  isOpen: boolean;
  onClose: () => void;
  onSelectOption?: (tier: 'budget' | 'recommended' | 'premium', price: number) => void;
}

export function EquipmentDetailsModal({ 
  equipmentId, 
  isOpen, 
  onClose,
  onSelectOption 
}: EquipmentDetailsModalProps) {
  
if (!isOpen) return null;

const mappedEquipmentId = ACTION_TO_EQUIPMENT_MAP[equipmentId] || equipmentId;
const details = equipmentDetails[mappedEquipmentId as keyof typeof equipmentDetails];

if (!details) return null;

  const recommendedOption = details.options.find(opt => opt.recommended);

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-xl sm:rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-blue-700 text-white p-4 sm:p-6 rounded-t-xl sm:rounded-t-2xl">
          <div className="flex justify-between items-start gap-3">
            <div className="flex-1 min-w-0">
              <h2 className="text-lg sm:text-xl lg:text-2xl font-bold mb-1 sm:mb-2 truncate">
                 {details.title || details.label || 'Détails de l\'équipement'}
              </h2>
              <div className="flex flex-wrap gap-2 sm:gap-4 text-xs sm:text-sm">
                <span className="flex items-center gap-1">
                  <TrendingUp className="w-3 h-3 sm:w-4 sm:h-4" />
                  {details.impact}
                </span>
                <span className="flex items-center gap-1">
                  <Euro className="w-3 h-3 sm:w-4 sm:h-4" />
                  {details.costRange}
                </span>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 sm:p-2 hover:bg-white/20 rounded-lg transition-colors flex-shrink-0"
            >
              <X className="w-5 h-5 sm:w-6 sm:h-6" />
            </button>
          </div>
        </div>

        <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
          {/* Options de prix */}
          <section>
            <h3 className="text-base sm:text-lg font-semibold text-gray-700 mb-3 sm:mb-4 flex items-center gap-2">
              <ThumbsUp className="w-4 h-4 sm:w-5 sm:h-5 text-blue-500" />
              Options disponibles
            </h3>
            <div className="grid gap-3 sm:gap-4">
              {details.options.map((option, index) => (
              <div
                key={index}
                className={`p-3 sm:p-4 rounded-lg sm:rounded-xl border-2 transition-all ${
                  option.recommended
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-blue-300'
                }`}
              >
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2 mb-2">
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <h4 className="text-sm sm:text-base font-semibold text-gray-900">
                        {option.name}
                      </h4>
                      {option.recommended && (
                        <span className="px-2 py-0.5 bg-blue-500 text-white text-xs font-medium rounded-full flex items-center gap-1">
                          <Check className="w-3 h-3" />
                          Recommandé
                        </span>
                      )}
                    </div>
                    <p className="text-xs sm:text-sm text-gray-600">{option.description}</p>
                  </div>
                  <div className="text-left sm:text-right sm:ml-4 mt-2 sm:mt-0">
                    <p className="text-xl sm:text-2xl font-bold text-blue-600">
                      {option.price === 0 ? 'Gratuit' : `${option.price}€`}
                    </p>
                    {option.setupFee && (
                      <p className="text-xs text-gray-500">
                        Frais installation : {option.setupFee}€
                      </p>
                    )}
                    {option.monthlyFee && (
                      <p className="text-xs text-gray-500">
                        + {option.monthlyFee}€/mois
                      </p>
                    )}
                  </div>
                </div>
                
                {/* NOUVEAU : Bouton de sélection */}
                {onSelectOption && (
  <button
    onClick={() => {
      onSelectOption(option.tier, option.price);
      onClose();
    }}
    className={`w-full mt-3 px-4 py-2 rounded-lg font-medium transition-colors ${
      option.recommended
        ? 'bg-blue-600 hover:bg-blue-700 text-white'
        : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
    }`}
  >
    Choisir cette option
  </button>
)}
              </div>
            ))}
            </div>
          </section>

          {/* Standards et certifications */}
          <section className="bg-gray-50 p-4 rounded-xl">
            <h3 className="text-lg font-semibold text-gray-700 mb-4 flex items-center gap-2">
              <Shield className="w-5 h-5 text-blue-600" />
              Standards de qualité
            </h3>
            <ul className="space-y-2">
              {details.standards.map((standard, index) => (
                <li key={index} className="flex items-start gap-2 text-sm text-gray-700">
                  <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                  <span>{standard}</span>
                </li>
              ))}
            </ul>
          </section>

          {/* Installation */}
          <section className="bg-amber-50 p-4 rounded-xl border border-amber-200">
            <h3 className="text-lg font-semibold text-gray-700 mb-4 flex items-center gap-2">
              <Wrench className="w-5 h-5 text-amber-600" />
              Installation
            </h3>
            <div className="grid md:grid-cols-3 gap-4 text-sm">
              <div>
                <p className="font-medium text-gray-700 mb-1">Professionnel requis</p>
                <p className={`font-semibold ${details.installation.professional ? 'text-orange-600' : 'text-green-600'}`}>
                  {details.installation.professional ? 'Oui, recommandé' : 'Non, installation par vous-même possible'}
                </p>
              </div>
              <div>
                <p className="font-medium text-gray-700 mb-1">Durée estimée</p>
                <p className="font-semibold text-gray-900">{details.installation.duration}</p>
              </div>
              <div>
                <p className="font-medium text-gray-700 mb-1">Garantie assurance</p>
                <p className="font-semibold text-blue-600">
                  {details.installation.insuranceDiscount === '0%' 
                    ? 'Aucune réduction' 
                    : `Réduction ${details.installation.insuranceDiscount}`}
                </p>
              </div>
            </div>
          </section>

          {/* Informations complémentaires */}
          <section>
            <h3 className="text-lg font-semibold text-gray-700 mb-4 flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-blue-600" />
              À savoir
            </h3>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-24 text-sm font-medium text-gray-600">Durée de vie</div>
                <div className="flex-1 text-sm text-gray-900">{details.additionalInfo.lifespan}</div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-24 text-sm font-medium text-gray-600">ROI sécuritaire</div>
                <div className="flex-1 text-sm text-gray-900">{details.additionalInfo.roi}</div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-24 text-sm font-medium text-gray-600">Aides / Crédits</div>
                <div className="flex-1 text-sm text-gray-900">{details.additionalInfo.subsidies}</div>
              </div>
            </div>
          </section>

          {/* CTA */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-xl border border-blue-200">
            <p className="text-sm text-gray-700 mb-4">
              <Sparkles className="w-4 h-4 inline-block mr-1 text-blue-600" /> <strong>Notre recommandation :</strong> {recommendedOption?.name} 
              pour un excellent rapport qualité/prix.
            </p>
            <button
              onClick={onClose}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-lg transition-colors"
            >
              J'ai compris
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}